import { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box, Paper, Typography, Button, IconButton, Tooltip, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TableSortLabel, TablePagination, Tabs, Tab, Toolbar, Dialog, DialogTitle,
    DialogContent, DialogContentText, DialogActions, TextField, Alert, Stack,
    FormControl, InputLabel, Select, MenuItem, InputAdornment
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import api from "../../api";
import SnackBarComponent from "../../components/SnackBarComponent";
import { useDebounce } from "../../hooks/useDebounce";

// ... Comparator Functions ...
function descendingComparator(a, b, orderBy) {
    let valA = a[orderBy];
    let valB = b[orderBy];

    if (orderBy === 'role_name') {
        valA = a.role_name || a.name || "";
        valB = b.role_name || b.name || "";
    }

    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();

    if (valB < valA) return -1;
    if (valB > valA) return 1;
    return 0;
}
function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
    { id: "city", label: "City" },
    { id: "vehicleModel", label: "Vehicle Model" },
    { id: "epochId", label: "Day & Date" },
    { id: "startTime", label: "Time" },
    { id: "available", label: "Available", align: "right" },
    { id: "booked", label: "Booked", align: "right" },
    { id: "reserved", label: "Reserved", align: "right" },
];

export default function HomeTestRideReservations() {
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);

    // Tab state: 0 = Current, 1 = Archive
    const [activeTab, setActiveTab] = useState(0);

    // Sorting state
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("city");

    // Search state
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [nameInputValue, setNameInputValue] = useState("");
    const [emailInputValue, setEmailInputValue] = useState("");
    const [phoneInputValue, setPhoneInputValue] = useState("");
    const [saving, setSaving] = useState(false);

    // Create Slot Dialog state
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newCity, setNewCity] = useState("");
    const [newVehicleModel, setNewVehicleModel] = useState("");
    const [newDate, setNewDate] = useState("");
    const [newStartTime, setNewStartTime] = useState("");
    const [newEndTime, setNewEndTime] = useState("");
    const [newTitle, setNewTitle] = useState("");
    const [newAvailable, setNewAvailable] = useState("");
    const [newReserved, setNewReserved] = useState("");
    const [creating, setCreating] = useState(false);
    const [cities, setCities] = useState([]);

    const dateToEpoch = (dateStr) => Math.floor(new Date(dateStr + "T00:00:00Z").getTime());

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchSlots = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/api/htr/slots");
            const list = res.data.data ?? [];
            setSlots(list);
        } catch (err) {
            showSnackbar("Failed to load slots.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSlots();
    }, [fetchSlots]);

    //Fetch Cities
    useEffect(() => {
        const fetchCities = async () => {
            setLoading(true);
            try {
                const res = await api.get("/api/htr/cities");
                const list = res.data.data;
                setCities(list);
            } catch (err) {
                console.error("Failed to fetch cities:", err);
                showSnackbar("Failed to load cities.", "error");
            } finally {
                setLoading(false);
            }
        };
        fetchCities();
    }, []);

    const modelList = ["X-47", "F77 Mach 2", "F77 Superstreet"];

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleTabChange = (_, newValue) => {
        setActiveTab(newValue);
        setPage(0);
        setSearchQuery("");
    };

    const handleAddClick = (slot) => {
        if (slot.available <= 0) {
            showSnackbar("No available slots to book or reserve.", "warning");
            return;
        }
        setSelectedSlot(slot);
        setNameInputValue("");
        setEmailInputValue("");
        setPhoneInputValue("");
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!selectedSlot) return;

        setSaving(true);
        try {
            const payload = {
                slot_key: selectedSlot.slot_key,
                datestamp: selectedSlot.datestamp,
                startTime: selectedSlot.startTime,
                endTime: selectedSlot.endTime,
                epochId: selectedSlot.epochId,
                name: nameInputValue.trim(),
                email: emailInputValue.trim(),
                phone: phoneInputValue.trim(),
            };

            const res = await api.post("/api/htr/slots/book", payload);
            showSnackbar(res.data?.msg || "Success!", "success");
            setDialogOpen(false);
            fetchSlots();
        } catch (err) {
            console.error("Save failed:", err);
            showSnackbar(err.response?.data?.msg || "Failed to process request.", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleCreateSlot = async () => {
        if (!newCity || !newVehicleModel || !newDate || !newStartTime || !newEndTime || !newAvailable || !newTitle) {
            showSnackbar("Please fill all required fields.", "warning");
            return;
        }

        setCreating(true);
        try {
            const payload = {
                city: newCity.trim(),
                vehicleModel: newVehicleModel.trim(),
                epochDate: dateToEpoch(newDate),
                startTime: newStartTime.trim(),
                endTime: newEndTime.trim(),
                available: Number(newAvailable),
                reserved: Number(newReserved),
                title: newTitle.trim()
            };
            const res = await api.post("/api/htr/slots", payload);
            showSnackbar(res.data?.msg || "Slot created successfully!", "success");
            setCreateDialogOpen(false);
            setNewCity("");
            setNewVehicleModel("");
            setNewDate("");
            setNewStartTime("");
            setNewEndTime("");
            setNewTitle("");
            setNewAvailable("");
            setNewReserved("");
            fetchSlots();
        } catch (err) {
            console.error("Failed to create slot:", err);
            showSnackbar(err.response?.data?.msg || "Failed to create slot.", "error");
        } finally {
            setCreating(false);
        }
    };

    const formatTime12h = (timeStr) => {
        if (!timeStr) return "";
        // Accepts "HHMM" (4-char) or "HH:MM" (5-char) format
        const clean = timeStr.replace(":", "");
        let hours = parseInt(clean.substring(0, 2), 10);
        const minutes = clean.substring(2, 4);
        const suffix = hours >= 12 ? "PM" : "AM";
        const hours12 = ((hours % 12) || 12).toString().padStart(2, "0");
        return `${hours12}:${minutes} ${suffix}`;
    };

    const formatEpochId = (epochId) => {
        if (!epochId) return "";
        try {
            const num = Number(epochId);
            if (!isNaN(num)) {
                return new Date(num).toLocaleString("en-US", { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' });
            }
        } catch (e) {
            console.error("Timestamp conversion error");
            showSnackbar("Timestamp conversion error");
        }
        return epochId;
    };

    // Filtered + sorted + paginated slots
    const visibleSlots = useMemo(() => {
        const todayMs = Date.now() - 86400000;
        const q = debouncedSearchQuery.toLowerCase();

        const filtered = slots.filter((s) => {
            // Tab filter
            const epochMs = Number(s.epochId);
            const isCurrentTab = activeTab === 0 ? epochMs >= todayMs : epochMs < todayMs;
            if (!isCurrentTab) return false;

            // Search filter
            if (!q) return true;
            return (
                (s.city || "").toLowerCase().includes(q) ||
                (s.vehicleModel || "").toLowerCase().includes(q) ||
                (s.startTime || "").toLowerCase().includes(q) ||
                (s.endTime || "").toLowerCase().includes(q) ||
                formatEpochId(s.epochId).toLowerCase().includes(q)
            );
        });
        return [...filtered].sort(getComparator(order, orderBy));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slots, debouncedSearchQuery, order, orderBy, activeTab]);

    const paginatedSlots = useMemo(() => {
        return visibleSlots.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [visibleSlots, page, rowsPerPage]);

    return (
        <Box sx={{ width: "100%" }}>
            {snackbar.open && (
                <SnackBarComponent
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                />
            )}

            <Paper sx={{ width: "100%", mb: 2 }}>
                <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "1 1 auto" }}>
                        <Typography variant="h6" component="div">
                            HOME TEST RIDE SLOTS
                        </Typography>
                    </Box>
                    <TextField
                        size="small"
                        placeholder="Search slots…"
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 240 }}
                    />
                    <Tooltip title="Create new slot">
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setCreateDialogOpen(true)}
                        >
                            Create Slot
                        </Button>
                    </Tooltip>
                </Toolbar>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="slot tabs">
                        <Tab label="Current" id="tab-current" aria-controls="tabpanel-current" />
                        <Tab label="Archive" id="tab-archive" aria-controls="tabpanel-archive" />
                    </Tabs>
                </Box>

                <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="slots table" size="medium">
                        <TableHead>
                            <TableRow>
                                {headCells.map((hc) => (
                                    <TableCell
                                        key={hc.id}
                                        align={hc.align || "left"}
                                        sortDirection={orderBy === hc.id ? order : false}
                                    >
                                        <TableSortLabel
                                            active={orderBy === hc.id}
                                            direction={orderBy === hc.id ? order : "asc"}
                                            onClick={() => handleRequestSort(hc.id)}
                                        >
                                            <strong>{hc.label}</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size="1.5rem" />
                                    </TableCell>
                                </TableRow>
                            ) : visibleSlots.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                        <Alert severity="info" sx={{ justifyContent: "center" }}>
                                            {searchQuery
                                                ? "No slots match your search."
                                                : activeTab === 0
                                                    ? "No current slots available."
                                                    : "No archived slots found."}
                                        </Alert>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedSlots.map((slot) => (
                                    <TableRow
                                        key={`${slot.slot_key}-${slot.datestamp}`}
                                        hover
                                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                    >
                                        <TableCell>{slot.city}</TableCell>
                                        <TableCell>{slot.vehicleModel}</TableCell>
                                        <TableCell>{formatEpochId(slot.epochId)}</TableCell>
                                        <TableCell>{formatTime12h(slot.startTime)} – {formatTime12h(slot.endTime)}</TableCell>
                                        <TableCell align="right">{slot.available}</TableCell>
                                        <TableCell align="right">{slot.booked}</TableCell>
                                        <TableCell align="right">{slot.reserved}</TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={slot.available > 0 ? "Book/Reserve Slot" : "No available slots"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleAddClick(slot)}
                                                        disabled={slot.available <= 0}
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={visibleSlots.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            {/* ── Booking / Reservation Dialog ── */}
            <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Book / Reserve Slot</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2, mt: 1 }}>
                        You are about to book/reserve a slot for <strong>{selectedSlot?.city} ({selectedSlot?.vehicleModel})</strong> at <strong>{formatEpochId(selectedSlot?.epochId)}</strong>.
                        <br /><br />
                        Fill in Name, Email and Phone Number to make a <strong>Booking</strong>. <br />
                        Leave them blank to make a <strong>Reservation</strong>.
                    </DialogContentText>

                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Name (Optional)"
                            fullWidth
                            value={nameInputValue}
                            onChange={(e) => setNameInputValue(e.target.value)}
                            disabled={saving}
                        />
                        <TextField
                            label="Email (Optional)"
                            type="email"
                            fullWidth
                            value={emailInputValue}
                            onChange={(e) => setEmailInputValue(e.target.value)}
                            disabled={saving}
                        />
                        <TextField
                            label="Phone Number (Optional)"
                            type="phone"
                            fullWidth
                            value={phoneInputValue}
                            onChange={(e) => setPhoneInputValue(e.target.value)}
                            disabled={saving}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {saving ? "Processing..." : "Confirm"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Create Slot Dialog ── */}
            <Dialog open={createDialogOpen} onClose={() => !creating && setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Slot</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2, mt: 1 }}>
                        Fill in the details below to create a new Home Test Ride slot.
                    </DialogContentText>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel id="city-select-label">Select City</InputLabel>
                            <Select
                                labelId="city-select-label"
                                id="city-select"
                                value={newCity}
                                label="Select City"
                                onChange={(e) => setNewCity(e.target.value)}
                                disabled={loading}
                            >
                                {loading ? (
                                    <MenuItem disabled>
                                        <CircularProgress size={16} sx={{ mr: 1 }} /> Loading cities…
                                    </MenuItem>
                                ) : cities.length === 0 ? (
                                    <MenuItem disabled>No cities available</MenuItem>
                                ) : (
                                    cities.map((city) => {
                                        const cityName = typeof city === "string" ? city : (city.city || city.name || JSON.stringify(city));
                                        return (
                                            <MenuItem key={cityName} value={cityName}>
                                                {cityName}
                                            </MenuItem>
                                        );
                                    })
                                )}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="vehicle-select-label">Select Vehicle Model</InputLabel>
                            <Select
                                labelId="vehicle-select-label"
                                id="vehicle-select"
                                value={newVehicleModel}
                                label="Select Vehicle Model"
                                onChange={(e) => setNewVehicleModel(e.target.value)}
                                disabled={creating}
                                required
                            >
                                {loading ? (
                                    <MenuItem disabled>
                                        <CircularProgress size={16} sx={{ mr: 1 }} /> Loading vehicles…
                                    </MenuItem>
                                ) : modelList.length === 0 ? (
                                    <MenuItem disabled>No vehicles available</MenuItem>
                                ) : (
                                    modelList.map((model) => (
                                        <MenuItem key={model} value={model}>
                                            {model}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            value={newDate}
                            onChange={(e) => setNewDate(e.target.value)}
                            disabled={creating}
                            required
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Start Time (24h Format)"
                                fullWidth
                                value={newStartTime}
                                onChange={(e) => setNewStartTime(e.target.value)}
                                disabled={creating}
                                required
                            />
                            <TextField
                                label="End Time (24h Format)"
                                fullWidth
                                value={newEndTime}
                                onChange={(e) => setNewEndTime(e.target.value)}
                                disabled={creating}
                                required
                            />
                        </Stack>
                        <TextField
                            label="Title"
                            fullWidth
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            disabled={creating}
                            required
                        />
                        <TextField
                            label="Total Available Slots"
                            type="number"
                            inputProps={{ min: 1 }}
                            fullWidth
                            value={newAvailable}
                            onChange={(e) => setNewAvailable(e.target.value)}
                            disabled={creating}
                            required
                        />
                        <TextField
                            label="Reserved Slots"
                            type="number"
                            inputProps={{ min: 0 }}
                            fullWidth
                            value={newReserved}
                            onChange={(e) => setNewReserved(e.target.value)}
                            disabled={creating}
                            required
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>Cancel</Button>
                    <Button
                        onClick={handleCreateSlot}
                        color="primary"
                        variant="contained"
                        disabled={creating || !newCity || !newVehicleModel || !newDate || !newStartTime || !newEndTime || !newAvailable || !newTitle}
                        startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {creating ? "Creating..." : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
